/**
 * Drawing a signature.
 *
 * The mark is a MANIFESTATION and never the authentication. What proves who
 * signed is the credential 21 CFR 11.200 asks for, verified by the server at
 * the moment of signing; this is what a reader recognises on the page
 * afterwards. A pad that was treated as proof would be the worst kind of
 * security theatre — anyone can draw anybody's name.
 *
 * Three things here are load-bearing rather than polish:
 *
 * **Pointer events, not mouse or touch.** One set of handlers covers a mouse,
 * a finger and a stylus, and `setPointerCapture` is what keeps a stroke
 * attached to the pad when the hand leaves it mid-letter — without it a
 * signature drawn quickly comes out cut off at the edge.
 *
 * **The canvas is sized in DEVICE pixels and scaled in CSS pixels.** A canvas
 * left at its CSS size draws a signature that is crisp on a laptop and a
 * blurred smear on a phone, and the blur is in the PNG that gets filed.
 *
 * **What comes out is a PNG data URL and nothing else**, because that is
 * exactly what the server accepts (`_check_ink`) and refuses anything else as.
 */
import React from 'react'

const BORDER = 'var(--border, #e4e7ec)'
const MUTED = 'var(--text-muted, #667085)'

export interface InkPadProps {
  /** Called with a `data:image/png;base64,...` URL, or '' when cleared. */
  onChange: (dataUrl: string) => void
  /** Drawn in the pad's own ink colour; defaults to the body text colour so a
   *  dark theme does not produce an invisible signature. */
  colour?: string
  heightPx?: number
  label?: string
  disabled?: boolean
}

export function InkPad({
  onChange, colour, heightPx = 120, label = 'Sign here', disabled = false,
}: InkPadProps) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const drawing = React.useRef(false)
  const marked = React.useRef(false)
  const [hasMark, setHasMark] = React.useState(false)

  // The stroke colour has to be a concrete value: a canvas cannot resolve
  // `var(--text)`, and passing one paints nothing at all — a pad that looks
  // like it accepted a signature and files an empty PNG.
  const strokeRef = React.useRef('#101828')
  const holderRef = React.useRef<HTMLDivElement | null>(null)

  const resize = React.useCallback(() => {
    const canvas = canvasRef.current
    const holder = holderRef.current
    if (!canvas || !holder) return
    const ratio = window.devicePixelRatio || 1
    const width = Math.max(1, Math.round(holder.clientWidth))
    // Resizing CLEARS the canvas, which is right: a signature drawn at one
    // width cannot be honestly rescaled to another, and a stretched mark is a
    // different mark. Anything drawn is dropped and the caller told, rather
    // than silently keeping a distorted copy.
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(heightPx * ratio)
    canvas.style.width = `${width}px`
    canvas.style.height = `${heightPx}px`
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.scale(ratio, ratio)
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = strokeRef.current
    }
    if (marked.current) {
      marked.current = false
      setHasMark(false)
      onChange('')
    }
  }, [heightPx, onChange])

  React.useEffect(() => {
    const holder = holderRef.current
    if (holder && typeof window !== 'undefined') {
      const computed = window.getComputedStyle(holder).color
      if (computed) strokeRef.current = colour || computed
    } else if (colour) {
      strokeRef.current = colour
    }
    resize()
    if (typeof window === 'undefined') return undefined
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [colour, resize])

  const at = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const box = canvas.getBoundingClientRect()
    return { x: event.clientX - box.left, y: event.clientY - box.top }
  }

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    // Keeps the stroke attached when the hand leaves the pad mid-letter.
    try { canvas.setPointerCapture(event.pointerId) } catch { /* older engines */ }
    drawing.current = true
    const { x, y } = at(event)
    ctx.beginPath()
    ctx.moveTo(x, y)
    // A tap with no movement is still a mark — a dot, an initial — so the dab
    // is drawn now rather than waiting for a move that may never come.
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || disabled) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = at(event)
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const end = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    drawing.current = false
    const canvas = canvasRef.current
    if (!canvas) return
    try { canvas.releasePointerCapture(event.pointerId) } catch { /* ignore */ }
    marked.current = true
    setHasMark(true)
    onChange(canvas.toDataURL('image/png'))
  }

  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const ratio = window.devicePixelRatio || 1
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio)
    marked.current = false
    setHasMark(false)
    onChange('')
  }

  return (
    <div ref={holderRef}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8,
                    marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 13, color: MUTED, flex: 1 }}>
          Draw with a mouse, a finger or a stylus
        </span>
        <button type="button" onClick={clear} disabled={disabled || !hasMark}
                style={{ fontSize: 13, background: 'none', border: 'none',
                         color: hasMark ? 'var(--accent, #175cd3)' : MUTED,
                         cursor: hasMark ? 'pointer' : 'default', padding: 0 }}>
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        style={{
          display: 'block', width: '100%', height: heightPx,
          border: `1px dashed ${BORDER}`, borderRadius: 6,
          background: 'var(--bg-primary, #fff)',
          // Without this a finger stroke scrolls the page instead of drawing.
          touchAction: 'none',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: disabled ? 0.6 : 1,
        }}
      />
      <p style={{ fontSize: 13, color: MUTED, margin: '4px 0 0' }}>
        The drawn mark appears on the document. It is not what proves who
        signed — the credential below is.
      </p>
    </div>
  )
}

export default InkPad
